import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Users, Search, ArrowLeft, UserPlus, Loader2 } from "lucide-react";
import Header from "@/components/Header";
import api from "@/lib/api";

export default function MyFilleuls() {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback((q) => {
    setLoading(true);
    api.get("/me/filleuls", { params: q ? { search: q } : {} })
      .then((r) => setData(r.data))
      .catch(() => setData({ total: 0, new_this_month: 0, items: [] }))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const t = setTimeout(() => load(search), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  return (
    <div className="min-h-screen sl-hero-bg pt-24 sm:pt-28 pb-24" data-testid="my-filleuls-page">
      <Header />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/parrainage" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-[var(--sl-primary)] mb-4" data-testid="back-to-parrainage">
          <ArrowLeft size={14} /> Retour au parrainage
        </Link>

        <div className="flex items-center gap-3 mb-6">
          <span className="w-12 h-12 rounded-xl bg-[var(--sl-primary)]/10 grid place-items-center text-[var(--sl-primary)]"><Users size={20} /></span>
          <div>
            <h1 className="font-heading text-2xl font-bold">Mes filleuls</h1>
            <p className="text-sm text-gray-500">La liste de vos filleuls directs.</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-5">
          <div className="sl-card !p-4 text-center" data-testid="filleuls-total">
            <div className="font-heading text-2xl sm:text-3xl font-bold text-[var(--sl-primary)]">{data?.total ?? "—"}</div>
            <div className="text-xs text-gray-500 mt-1">Filleuls au total</div>
          </div>
          <div className="sl-card !p-4 text-center" data-testid="filleuls-new-this-month">
            <div className="font-heading text-2xl sm:text-3xl font-bold text-emerald-700 flex items-center justify-center gap-1">
              <UserPlus size={18} /> {data?.new_this_month ?? "—"}
            </div>
            <div className="text-xs text-gray-500 mt-1">Nouveaux ce mois-ci</div>
          </div>
        </div>

        <div className="relative mb-4">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un filleul par nom…"
            className="w-full rounded-xl border border-gray-200 bg-white pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--sl-primary)]/30 focus:border-[var(--sl-primary)]"
            data-testid="filleuls-search-input"
          />
        </div>

        <div className="sl-card !p-0 overflow-hidden" data-testid="filleuls-list">
          {loading ? (
            <div className="grid place-items-center py-14"><Loader2 className="animate-spin text-[var(--sl-primary)]" /></div>
          ) : !data || data.items.length === 0 ? (
            <div className="text-center text-sm text-gray-400 py-10" data-testid="filleuls-empty">
              {search ? "Aucun filleul ne correspond à cette recherche." : "Vous n'avez pas encore de filleul. Partagez votre code pour commencer !"}
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {data.items.map((f, i) => (
                <li key={i} className="px-4 sm:px-5 py-3.5 flex items-center gap-3" data-testid={`filleul-row-${i}`}>
                  <span className="w-9 h-9 rounded-full bg-[var(--sl-primary)]/10 text-[var(--sl-primary)] grid place-items-center text-sm font-bold flex-shrink-0">
                    {(f.name || "?").charAt(0).toUpperCase()}
                  </span>
                  <span className="font-medium text-sm truncate">{f.name}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <p className="text-xs text-gray-400 mt-4 text-center">
          Pour des raisons de confidentialité, seuls les noms de vos filleuls sont visibles — montants, soldes et transactions restent privés.
        </p>
      </div>
    </div>
  );
}
